import moment from "moment";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import useAuthorizedAction, {
  AuthorizedActionsKeys,
} from "../../../hooks/useAuthorizedAction";
import { IEcgsFilter } from "../../../models/ecg.interface";
import { Button } from "@shltelemedicine/shl-ui-lib";
import CheckBoxFormInput from "../CheckBox/CheckBoxFormInput";
import DateFormInput from "../DateInput/DateFormInput";
import TextFormInput from "../TextInput/TextFormInput";

export type EcgsFilters = Omit<IEcgsFilter, "search">;
type EcgsFiltersForm = {
  unviewedEcgs: boolean;
  reviewed: boolean;
  bySerialNumber: boolean;
  serialNumber: string | undefined;
  fromDate?: string | undefined;
  toDate?: string | undefined;
  includeArchived: boolean;

  reviewPriorityNone: boolean;
  reviewPriorityLow: boolean;
  reviewPriorityHigh: boolean;
};
type EcgsFilterProps = {
  filters?: EcgsFilters;
  onFilter?: (filters: EcgsFilters) => void;
};

const EcgsFilter = ({ filters, onFilter }: EcgsFilterProps) => {
  const intl = useIntl();

  const isReviewer = useAuthorizedAction(AuthorizedActionsKeys.ReviewEcg);

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors: formErrors },
  } = useForm<EcgsFiltersForm>({
    defaultValues: getFormInitValues(filters),
  });

  const onSubmit = async (filters: EcgsFiltersForm) => {
    if (onFilter) {
      if (!filters.bySerialNumber) {
        filters.serialNumber = undefined;
      }

      const {
        unviewedEcgs,
        reviewed,
        serialNumber,
        fromDate,
        toDate,
        includeArchived,
        reviewPriorityNone,
        reviewPriorityLow,
        reviewPriorityHigh,
      } = filters;

      onFilter({
        unviewedOnly: isReviewer ? undefined : unviewedEcgs,
        unInterpretedOnly: isReviewer ? reviewed : undefined,
        sn: serialNumber,
        fromDate,
        toDate,
        includeArchived: !isReviewer ? includeArchived : undefined,
        reviewPriorityNone: isReviewer ? reviewPriorityNone : undefined,
        reviewPriorityLow: isReviewer ? reviewPriorityLow : undefined,
        reviewPriorityHigh: isReviewer ? reviewPriorityHigh : undefined,
      });
    }
  };

  const fromDate = watch("fromDate");
  const toDate = watch("toDate");
  const bySerialNumber = watch("bySerialNumber");

  const fromTranslated = intl.messages["ECGS_FILTER.DATE.FROM"] as string;
  const toTranslated = intl.messages["ECGS_FILTER.DATE.TO"] as string;
  const insertDeviceSnTranslated = intl.messages[
    "ECGS_FILTER.INSERT_DEVICE_SN"
  ] as string;

  return (
    <Form className="p-2 font-md">
      {isReviewer && (
        <>
          {/* Time to review (Will be added in the future)*/}
          {/* <Form.Row className="align-items-center mb-2">
            <Col>
              <div className="d-flex align-items-center">
                <CheckBoxFormInput
                  name={"reviewPriorityHigh"}
                  control={control}
                  input={{
                    label: (
                      <ReviewPriorityIndicator
                        priority={"high"}
                        showTimeSymbol
                      />
                    ),
                  }}
                />
                <span className="mx-2"></span>
                <CheckBoxFormInput
                  name={"reviewPriorityLow"}
                  control={control}
                  input={{
                    label: (
                      <ReviewPriorityIndicator
                        priority={"low"}
                        showTimeSymbol
                      />
                    ),
                  }}
                />
                <span className="mx-2"></span>
                <CheckBoxFormInput
                  name={"reviewPriorityNone"}
                  control={control}
                  input={{
                    label: (
                      <ReviewPriorityIndicator
                        priority={"none"}
                        showTimeSymbol
                      />
                    ),
                  }}
                />
              </div>
            </Col>
          </Form.Row> */}
          <Form.Row className="align-items-center mb-2">
            <Col>
              <CheckBoxFormInput
                name={"reviewed"}
                control={control}
                input={{
                  label: (
                    <FormattedMessage
                      id="ECGS_FILTER.UNINTERPRETED_ONLY"
                      defaultMessage="Uninterpreted Only"
                    />
                  ),
                }}
              />
            </Col>
          </Form.Row>
        </>
      )}

      {/* Unviewed (Will be added in the future)*/}
      {/* <Form.Row className="align-items-center mb-2">
        <Col>
          <CheckBoxFormInput
            name={"unviewedEcgs"}
            control={control}
            input={{
              label: (
                <div className="d-flex align-items-center">
                  <CircleIndicator />
                  <span className="mx-sm"></span>
                  <FormattedMessage
                    id="ECGS_FILTER.UNVIEWED_ECG"
                    defaultMessage="Unviewed ECG"
                  />
                </div>
              ),
            }}
          />
        </Col>
      </Form.Row> */}
      {/* with Interpretation (Will be added in the future)*/}
      {/* <Form.Row className="align-items-center mb-2">
        <Col>
          <CheckBoxFormInput
            name={"interpretation"}
            control={control}
            input={{
              label: (
                <div className="d-flex align-items-center">
                  <InterpretationIcon selected />
                  <span className="mx-sm"></span>
                  <FormattedMessage
                    id="ECGS_FILTER.WITH_INTERPRETATION"
                    defaultMessage="With Interpretation"
                  />
                </div>
              ),
            }}
          />
        </Col>
      </Form.Row> */}
      <Form.Row className="align-items-center mb-1">
        <Col>
          {/* By SerialNumber */}
          <CheckBoxFormInput
            name={"bySerialNumber"}
            control={control}
            input={{
              label: (
                <FormattedMessage
                  id="ECGS_FILTER.BY_SN"
                  defaultMessage="By SN"
                />
              ),
            }}
          />
        </Col>
      </Form.Row>
      <Form.Row className="align-items-center mb-2">
        <Col>
          {/* SerialNumber */}
          <TextFormInput
            name={"serialNumber"}
            control={control}
            rules={{
              required: bySerialNumber,
            }}
            input={{
              isInvalid: !!formErrors?.serialNumber,
              placeholder: insertDeviceSnTranslated,
              disabled: !bySerialNumber,
            }}
          />
        </Col>
      </Form.Row>
      <Form.Row className="align-items-center mb-3">
        <Col>
          {/* FromDate */}
          <Form.Label>
            <FormattedMessage
              id="ECGS_FILTER.DATE.FROM"
              defaultMessage="From"
            />
          </Form.Label>
          <DateFormInput
            name={"fromDate"}
            control={control}
            input={{
              placeholder: fromTranslated,
              isInvalid: !!formErrors?.fromDate,
              max: toDate ? moment(toDate).toDate() : new Date(),
              isClearable: false,
            }}
          />
        </Col>
        <Col>
          {/* ToDate */}
          <Form.Label>
            <FormattedMessage id="ECGS_FILTER.DATE.TO" defaultMessage="To" />
          </Form.Label>
          <DateFormInput
            name={"toDate"}
            control={control}
            input={{
              placeholder: toTranslated,
              isInvalid: !!formErrors?.toDate,
              max: new Date(),
              min: fromDate ? moment(fromDate).toDate() : undefined,
              isClearable: false,
            }}
          />
        </Col>
      </Form.Row>
      {!isReviewer && (
        <Form.Row className="align-items-center mb-1">
          <Col>
            {/* By Archive */}
            <CheckBoxFormInput
              name={"includeArchived"}
              control={control}
              input={{
                label: (
                  <FormattedMessage
                    id="ECGS_FILTER.BY_ARCHIVED"
                    defaultMessage="View Archived ECGs"
                  />
                ),
              }}
            />
          </Col>
        </Form.Row>
      )}
      <Button
        type="submit"
        className="px-4 ml-auto"
        bgColor="#435FC5"
        onClick={handleSubmit(onSubmit)}
      >
        <FormattedMessage id="ECGS_FILTER.SHOW" defaultMessage="Show" />
      </Button>
    </Form>
  );
};

export default EcgsFilter;

const getFormInitValues = (filters?: EcgsFilters): EcgsFiltersForm => {
  return {
    reviewed: !!filters?.unInterpretedOnly,
    unviewedEcgs: !!filters?.unviewedOnly,
    reviewPriorityHigh: !!filters?.reviewPriorityHigh,
    reviewPriorityLow: !!filters?.reviewPriorityLow,
    reviewPriorityNone: !!filters?.reviewPriorityNone,

    bySerialNumber: !!filters?.sn,
    serialNumber: filters?.sn,
    includeArchived: !!filters?.includeArchived,

    fromDate: filters?.fromDate,
    toDate: filters?.toDate,
  };
};
